import dropin, { Dropin } from "braintree-web-drop-in";
import React from "react";
import { ApplicationContext } from "../../../ApplicationContext";

type BraintreeDropinProps = {
    clientToken: string | null;
    amount: string | null;
    setBraintreeInstance: (dropIn: Dropin) => void;
};

const BraintreeDropin: React.FC<BraintreeDropinProps> = ({
                                                             clientToken,
                                                             amount,
                                                             setBraintreeInstance,
                                                         }) => {
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const instanceRef = React.useRef<Dropin | null>(null);
    const creatingRef = React.useRef(false);
    const mountedRef = React.useRef(true);

    const applicationContext = React.useContext(ApplicationContext)

    // Debounce the payment amount so it doesn't re-render too quickly on price change
    const [debouncedAmount, setDebouncedAmount] = React.useState(amount);

    React.useEffect(() => {
        const id = setTimeout(() => setDebouncedAmount(amount), 400);
        return () => clearTimeout(id);
    }, [amount]);

    const teardown = React.useCallback(async () => {
        if (instanceRef.current) {
            try {
                await instanceRef.current.teardown();
            } catch {}
            instanceRef.current = null;
        }
    }, []);

    const ios = isIOS();
    const enableGooglePay = !ios && applicationContext.appConfig.braintreeConfiguration?.google?.enable

    const create = React.useCallback(async () => {
        if (!containerRef.current || creatingRef.current) return;

        creatingRef.current = true;

        const config: any = {
            authorization: clientToken,
            container: containerRef.current,
            threeDSecure: true,
            ...(enableGooglePay
                ? {
                    googlePay: {
                        googlePayVersion: 2,
                        ...(applicationContext.appConfig.braintreeConfiguration?.google?.merchantId
                            ? { merchantId: applicationContext?.appConfig.braintreeConfiguration?.google?.merchantId }
                            : {}),
                        transactionInfo: {
                            totalPriceStatus: "FINAL",
                            totalPrice: debouncedAmount,
                            currencyCode: "GBP",
                            countryCode: "GB"
                        }
                    }
                } : {}),
            ...(applicationContext.appConfig.braintreeConfiguration?.paypal?.enable
                ? {
                    paypal: {
                        flow: "checkout",
                        amount: debouncedAmount,
                        currency: "GBP",
                        enableShippingAddress: false
                    },
                } : {}),
            ...(applicationContext.appConfig.braintreeConfiguration?.apple?.enable
                ? {
                    applePay: {
                        displayName: "Superdrug Mobile",
                        paymentRequest: {
                            total: {
                                label: "Superdrug Mobile",
                                amount: debouncedAmount
                            },
                            countryCode: "GB",
                            currencyCode: "GBP",
                            supportedNetworks: ["visa", "masterCard"],
                            merchantCapabilities: ["supports3DS", "supportsCredit", "supportsDebit"]
                        }
                    }
                } : {})
        };

        try {
            const instance = await dropin.create(config);

            if (!mountedRef.current) {
                await instance.teardown();
                return;
            }

            instanceRef.current = instance;
            setBraintreeInstance(instance)
        } catch (e) {
            console.error("Drop-in create error", e);
        } finally {
            creatingRef.current = false;
        }
    }, [clientToken, debouncedAmount, applicationContext, setBraintreeInstance]);

    // Initial creation
    React.useEffect(() => {
        create();
        return () => {
            mountedRef.current = false;
            teardown();
        };
    }, []);

    // Recreate when debounced amount changes
    React.useEffect(() => {
        if (!instanceRef.current) return;

        const recreate = async () => {
            await teardown();
            await create();
        };

        recreate();
    }, [debouncedAmount]);

    return <div ref={containerRef} />;
};

/**
 * Checks if the browser making the request is on the iOS device.
 * It also handles the iPadOS Safari pretending to be macOS.
 */
function isIOS(): boolean {
    if (typeof navigator === "undefined") return false;

    return (
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
}

export default BraintreeDropin;